import { session, SESSION_KEYS } from 'utils/storage';

export const MARKETING_PARAM_KEYS = [
  'fbclid',
  'gclid',
  'li_fat_id',
  'msclkid',
  'ttclid',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term'
] as const;

export const MARKETING_PARAM_PREFIXES = ['banner_'] as const;

const FRESH_CAMPAIGN_KEYS = ['utm_campaign', 'gclid', 'fbclid'] as const;

export type MarketingParams = Record<string, string>;

export const isMarketingKey = (key: string): boolean =>
  (MARKETING_PARAM_KEYS as readonly string[]).includes(key) ||
  MARKETING_PARAM_PREFIXES.some((prefix) => key.startsWith(prefix) && key.length > prefix.length);

export const extractMarketingParams = (params: URLSearchParams): MarketingParams => {
  const out: MarketingParams = {};
  params.forEach((value, key) => {
    if (value && isMarketingKey(key)) out[key] = value;
  });
  return out;
};

export const isFreshCampaign = (incoming: MarketingParams): boolean =>
  FRESH_CAMPAIGN_KEYS.some((key) => Boolean(incoming[key]));

export const computeNextStored = (
  stored: MarketingParams,
  incoming: MarketingParams
): MarketingParams => {
  if (Object.keys(incoming).length === 0) return stored;
  if (isFreshCampaign(incoming)) return { ...incoming };
  return { ...stored, ...incoming };
};

export const shallowEqualParams = (a: MarketingParams, b: MarketingParams): boolean => {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

export const readStoredMarketingParams = (): MarketingParams => {
  const raw = session.get(SESSION_KEYS.MARKETING_PARAMS);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const sanitized: MarketingParams = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'string' && value && isMarketingKey(key)) {
        sanitized[key] = value;
      }
    }
    return sanitized;
  } catch {
    return {};
  }
};

export const writeStoredMarketingParams = (params: MarketingParams): void => {
  session.set(SESSION_KEYS.MARKETING_PARAMS, JSON.stringify(params));
};

export const mergeStoredIntoParams = (
  current: URLSearchParams,
  stored: MarketingParams
): URLSearchParams | null => {
  const next = new URLSearchParams(current);
  let changed = false;
  for (const [key, value] of Object.entries(stored)) {
    if (!next.has(key)) {
      next.set(key, value);
      changed = true;
    }
  }
  if (!changed) return null;
  return next;
};
